import { useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi } from '@/api/planned';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { toCivil } from '@/features/envelope/window';
import { plannedErrorKey } from './plannedErrors';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const TITLE_ID = 'coverage-title';

interface CoverageDialogProps {
  account: Account;
  onClose: () => void;
  onSaved: (message: string) => void;
  onSubscriptionRequired: () => void;
}

/**
 * Account coverage frontier (TMMAPP-0009, MM-D-57): POST …/coverage with WIPE | KEEP.
 */
export function CoverageDialog({
  account,
  onClose,
  onSaved,
  onSubscriptionRequired,
}: CoverageDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const current = account.coveredThrough;
  const [date, setDate] = useState(current ?? toCivil(new Date()));
  const [policy, setPolicy] = useState<'WIPE' | 'KEEP'>('WIPE');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const backward = current !== null && date < current;
  const { dirty } = useDirty({ date, policy });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (backward || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await plannedApi.coverage(account.id, {
        coveredThrough: date,
        uncoveredPlanned: policy,
      });
      onSaved(
        t('accounts.coverageResult', {
          superseded: result.superseded,
          missed: result.missed,
        }),
      );
      onClose();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (isApiError(err) && err.code === ErrorCodes.COVERAGE_BACKWARD) {
        setError(t('accounts.coverageBackward'));
      } else {
        setError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
      }
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog guard={guard} titleId={TITLE_ID} fullWidth maxWidth="sm">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <DialogTitle id={TITLE_ID}>{t('accounts.coverageTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {current !== null
              ? t('accounts.coveredThrough', { date: formatCivilDate(current, locale) })
              : t('accounts.coveredThroughNone')}
          </Typography>
          {error !== null && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            type="date"
            label={t('accounts.coverageDate')}
            value={date}
            onChange={(event) => setDate(event.target.value)}
            required
            fullWidth
            error={backward}
            helperText={backward ? t('accounts.coverageBackward') : undefined}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>{t('accounts.coverageTitle')}</FormLabel>
            <RadioGroup
              value={policy}
              onChange={(_event, value) => setPolicy(value as 'WIPE' | 'KEEP')}
            >
              <FormControlLabel
                value="WIPE"
                control={<Radio />}
                label={t('accounts.coverageWipe')}
              />
              <FormControlLabel
                value="KEEP"
                control={<Radio />}
                label={t('accounts.coverageKeep')}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
          <SubmitButton type="submit" disabled={backward} loading={submitting}>
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
